<template>
  <!-- We use flexbox grid here to stick to legacy grid
  Once we fully migrate and redesign the page
  we can remove the related classes and use our own container system
  -->
  <div class="settings-auth-accounts middle-container">
    <div class="col-xs-12 col-sm-10">
      <h4 class="settings-auth-accounts__headline">
        {{ $t("headline") }}
      </h4>

      <settings-loader :loading="loading" :error-message="errorMessage" />
      <div v-if="!loading && !errorMessage">
        <social-account
          v-for="account in socialAccounts"
          :key="account.type"
          :account="account"
          :can-unlink="!!blinkistAccount"
          @unlink-social-account="triggerUnlinkModal"
        />
        <sso-account v-if="ssoAccount" :account="ssoAccount" />
        <blinkist-account
          :account="blinkistAccount"
          v-if="!denyNonSamlAccounts"
          class="mb-16"
          @change-password="triggerModal('change-password-modal')"
          @change-email="triggerModal('change-email-modal')"
          @add-login="triggerModal('add-login-modal')"
        />
        <b2b-promotion-banner user-segment="premium" v-if="showB2bBanner" />
      </div>

      <unlink-social-modal :account="accountToUnlink" @submit="unlinkSocialAccount" />
      <add-login-modal @submit="addLogin" />
      <change-password-modal :has-temp-password="hasTempPassword" @submit="changePassword" />
      <change-email-modal
        v-if="blinkistAccount && blinkistAccount.email"
        :email="blinkistAccount.email"
        @submit="changeEmail"
      />

      <status-modal
        id="status-modal"
        :status="statusModalType"
        :message="statusModalMessage"
        :headline="statusModalHeadline"
        :button-type="statusModalButtonType"
        @closed="statusModalClosed"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"

import SocialAccount from "@/features/settings/components/social-account"
import SsoAccount from "@/features/settings/components/sso-account"
import BlinkistAccount from "@/features/settings/components/blinkist-account"
import StatusModal from "@/features/settings/components/modals/status-modal"
import ChangePasswordModal from "@/features/settings/components/modals/change-password-modal"
import B2bPromotionBanner from "@/features/b2b/components/promotion-banner"
import ChangeEmailModal from "@/features/settings/components/modals/change-email-modal"
import AddLoginModal from "@/features/settings/components/modals/add-login-modal"
import UnlinkSocialModal from "@/features/settings/components/modals/unlink-social-modal"
import settingsApi from "@/api/settings"
import events from "@/bl-events/index"
import { reloadPage } from "@/utils/location"
import SettingsLoader from "@/features/settings/components/settings-loader"

const TRACKING = {
  PAGE: "settings",
  ERROR: "error",
  SUCCESS: "success"
}

export default {
  name: "AuthenticationAccounts",
  components: {
    SettingsLoader,
    BlinkistAccount,
    SocialAccount,
    SsoAccount,
    StatusModal,
    B2bPromotionBanner,
    AddLoginModal,
    ChangeEmailModal,
    ChangePasswordModal,
    UnlinkSocialModal
  },
  props: {
    hasTempPassword: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    loading: false,
    accounts: {},
    denyNonSamlAccounts: false,
    accountToUnlink: null,
    errorMessage: null,

    statusModalType: "success",
    reloadOnStatusModalClosed: false,
    statusModalMessage: "",
    statusModalHeadline: "",
    statusModalButtonType: "close"
  }),
  computed: {
    ...mapGetters(["isInB2bOrganisation", "isSubscriptionPartnerUser"]),
    ...mapGetters(["hasUserAccessToContent"]),
    showB2bBanner() {
      return !this.isInB2bOrganisation && !this.isSubscriptionPartnerUser
    },
    socialAccounts() {
      let socialAccounts = []
      const showableSocialAccountTypes = ["apple", "facebook"]
      Object.keys(this.accounts).forEach((key) => {
        if (showableSocialAccountTypes.indexOf(key) > -1 && this.accounts[key]) {
          socialAccounts.push({
            type: key,
            email: this.accounts[key]
          })
        }
      })
      return socialAccounts
    },
    blinkistAccount() {
      if (this.accounts.blinkist) {
        return {
          type: "blinkist",
          email: this.accounts.blinkist
        }
      } else {
        return null
      }
    },
    ssoAccount() {
      if (this.accounts.auth0) {
        return {
          type: "auth0",
          email: this.accounts.auth0
        }
      } else {
        return null
      }
    }
  },
  mounted() {
    this.organisationInfo()
    return this.loadAccounts()
  },
  methods: {
    ...mapActions(["organisationInfo"]),
    triggerModal(modalId) {
      events.emit(`modal:${modalId}:open`)
      events.track(TRACKING.PAGE, modalId)
    },
    triggerUnlinkModal(account) {
      this.accountToUnlink = account
      this.triggerModal("unlink-social-modal")
    },
    triggerStatusModal(status, type, reloadOnClose, messageOverride) {
      this.statusModalType = status
      this.reloadOnStatusModalClosed = reloadOnClose
      this.statusModalButtonType = reloadOnClose ? "continue" : "close"
      this.statusModalHeadline = this.$t(`${this.statusModalType}.${type}.headline`)
      this.statusModalMessage = messageOverride || this.$t(`${this.statusModalType}.${type}.message`)
      this.triggerModal("status-modal")
    },
    statusModalClosed() {
      if (this.reloadOnStatusModalClosed) {
        reloadPage()
      }
    },
    loadAccounts() {
      this.loading = true
      this.errorMessage = null

      return settingsApi
        .loadAccounts()
        .then((response) => {
          this.accounts = response.accounts
          this.denyNonSamlAccounts = response.denyNonSamlAccounts
          this.loading = false
        })
        .catch(() => {
          this.accounts = {}
          this.loading = false
          this.errorMessage = this.$t("error.loadingAccounts")
        })
    },
    changePassword({ oldPassword, newPassword }) {
      /* eslint-disable-line object-curly-newline */
      settingsApi
        .changePassword(oldPassword, newPassword)
        .then(() => {
          this.triggerStatusModal("success", "changePassword", true)
          events.track(TRACKING.PAGE, "change-password", TRACKING.SUCCESS)
        })
        .catch(() => {
          this.triggerStatusModal("error", "changePassword")
          events.track(TRACKING.PAGE, "change-password", TRACKING.ERROR)
        })
    },
    changeEmail({ newEmail, password }) {
      /* eslint-disable-line object-curly-newline */
      settingsApi
        .changeEmail(newEmail, password)
        .then(this.loadAccounts)
        .then(() => {
          this.triggerStatusModal("success", "changeEmail")
          events.track(TRACKING.PAGE, "change-email", TRACKING.SUCCESS)
        })
        .catch((error) => {
          const apiMessage = error && error.content && error.content.message
          this.triggerStatusModal("error", "changeEmail", false, apiMessage)
          events.track(TRACKING.PAGE, "change-email", TRACKING.ERROR)
        })
    },
    addLogin({ email, password }) {
      /* eslint-disable-line object-curly-newline */
      settingsApi
        .addLogin(email, password)
        .then(() => {
          this.triggerStatusModal("success", "addLogin", true)
          events.track(TRACKING.PAGE, "add-login", TRACKING.SUCCESS)
        })
        .catch(() => {
          this.triggerStatusModal("error", "addLogin")
          events.track(TRACKING.PAGE, "add-login", TRACKING.ERROR)
        })
    },
    unlinkSocialAccount(account) {
      settingsApi
        .unlinkSocialAccount(account.type)
        .then(this.loadAccounts)
        .then(() => {
          this.triggerStatusModal("success", `unlinkSocialAccount.${account.type}`)
          events.track(TRACKING.PAGE, `unlink-social-account-${account.type}`, TRACKING.SUCCESS)
        })
        .catch(() => {
          this.triggerStatusModal("error", `unlinkSocialAccount.${account.type}`)
          events.track(TRACKING.PAGE, `unlink-social-account-${account.type}`, TRACKING.ERROR)
        })
    }
  },
  i18n: {
    messages: {
      en: {
        headline: "Login variants",
        loading: "Loading...",
        error: {
          loadingAccounts: "Could not load list of accounts.",
          changePassword: {
            headline: "Password could not be changed",
            message:
              "This didn’t work. Try again and make sure you used the correct old password, and your new password is between 8 and 72 characters long."
          },
          changeEmail: {
            headline: "Email address could not be changed",
            message: "This didn’t work. Try again and make sure you typed in a valid email address."
          },
          addLogin: {
            headline: "Email login details could not be linked",
            message: "This didn’t work. Make sure you typed in a valid email address and try again."
          },
          unlinkSocialAccount: {
            apple: {
              headline: '"Sign in with Apple" could not be removed',
              message: "This didn’t work. Please try again later or contact our customer support."
            },
            facebook: {
              headline: '"Sign in with Facebook" could not be removed',
              message: "This didn’t work. Please try again later or contact our customer support."
            }
          }
        },
        success: {
          changePassword: {
            headline: "Password changed",
            message:
              "This worked. From now on you can use your new password. For your own security you'll be logged out from all connected devices."
          },
          changeEmail: {
            headline: "Email address changed",
            message: "Success! From now on you can use your updated email address to login."
          },
          addLogin: {
            headline: "Email login details linked",
            message:
              "Success! As an additional login variant you can now use your email address and password. For your own security you'll be logged out from all devices shortly."
          },
          unlinkSocialAccount: {
            apple: {
              headline: "Success, this worked!",
              message:
                '"Sign in with Apple" has been removed from your Blinkist account. Now you can log in with your email address and your password only.'
            },
            facebook: {
              headline: "Success, this worked!",
              message:
                '"Sign in with Facebook" has been removed from your Blinkist account. Now you can log in with your email address and your password only.'
            }
          }
        }
      },
      de: {
        headline: "Konten",
        loading: "Lade...",
        error: {
          loadingAccounts: "Konten konnten nicht geladen werden.",
          changePassword: {
            headline: "Passwort konnte nicht geändert werden",
            message:
              "Entschuldigung, etwas ist schief gelaufen. Versuch’s nochmal und stelle sicher, dass du dein aktuelles Passwort korrekt eingegeben hast und dein neues Passwort zwischen 8 und 72 Zeichen lang ist."
          },
          changeEmail: {
            headline: "E-Mail-Adresse konnte nicht geändert werden",
            message: "Etwas ist schiefgelaufen. Hast du eine korrekte E-Mail-Adresse eingegeben?"
          },
          addLogin: {
            headline: "E-Mail-Adresse konnte nicht hinzugefügt werden",
            message: "Bitte überprüfe die eingegebene E-Mail-Adresse und versuche es nochmal."
          },
          unlinkSocialAccount: {
            apple: {
              headline: '"Mit Apple anmelden" konnte nicht entfernt werden',
              message:
                "Das hat leider nicht funktioniert. Versuch’s bitte nochmal oder benachrichtige unseren Kundenservice."
            },
            facebook: {
              headline: '"Mit Facebook anmelden" konnte nicht entfernt werden',
              message:
                "Das hat leider nicht funktioniert. Versuch’s bitte nochmal oder benachrichtige unseren Kundenservice."
            }
          }
        },
        success: {
          changePassword: {
            headline: "Passwort geändert",
            message:
              "Hurra, du kannst jetzt dein neues Passwort nutzen. Zu deiner eigenen Sicherheit wirst du zuvor von allen verbundenen Geräten ausgeloggt."
          },
          changeEmail: {
            headline: "E-Mail-Adresse geändert",
            message: "Hurra, du kannst dich jetzt mit deiner neuen E-Mail-Adresse einloggen."
          },
          addLogin: {
            headline: "Konto-Login-Details erfolgreich hinzugefügt",
            message:
              "Hurra, du kannst dich jetzt mit deiner neuen E-Mail-Adresse und dem neuen Passwort einloggen. Zu deiner eigenen Sicherheit wirst du zuvor von allen verbundenen Geräten ausgeloggt."
          },
          unlinkSocialAccount: {
            apple: {
              headline: '"Mit Apple anmelden" entfernt',
              message:
                '"Mit Apple anmelden" wurde erfolgreich von deinem Blinkist-Konto entfernt und du kannst dich nur noch mit deiner E-Mail-Adresse und deinem Passwort einloggen.'
            },
            facebook: {
              headline: '"Mit Facebook anmelden" entfernt',
              message:
                '"Mit Facebook anmelden" wurde erfolgreich von deinem Blinkist-Konto entfernt und du kannst dich nur noch mit deiner E-Mail-Adresse und deinem Passwort einloggen.'
            }
          }
        }
      },
      es: {
        error: {
          changeEmail: {
            headline: "No se pudo cambiar la dirección de correo electrónico",
            message:
              "Algo salió mal. Inténtalo de nuevo y asegúrate de haber introducido una dirección de correo electrónico válida."
          }
        },
        success: {
          changeEmail: {
            headline: "Dirección de correo electrónico cambiada",
            message:
              "¡Listo! A partir de ahora puedes usar tu nueva dirección de correo electrónico para iniciar sesión."
          }
        }
      },
      pt: {
        error: {
          changeEmail: {
            headline: "Não foi possível alterar o endereço de email",
            message: "Algo deu errado. Tente novamente e verifique se você digitou um endereço de email válido."
          }
        },
        success: {
          changeEmail: {
            headline: "Endereço de email alterado",
            message: "Pronto! A partir de agora você pode usar seu novo endereço de email para fazer login."
          }
        }
      }
    }
  }
}
</script>

<style lang="scss">
.settings-auth-accounts {
  @include font-family(sans-serif);

  &__headline {
    @include font-style(h4);

    margin-bottom: $spacing-32;
  }
}
</style>
